<script>
import { compareRows } from '@site/data/compare'
import { useConsult } from '@site/composables/useConsult'
import AppIcon from '@site/components/common/AppIcon.vue'

export default {
  name: 'CompareView',
  components: { AppIcon },
  data() {
    const { open } = useConsult()
    return {
      open,
      compareRows,
      // Nova 旗舰差异化卖点（对应新定位：性能 / 便捷 / 迭代）
      novaEdges: [
        { icon: 'rocket', title: '更快的性能', desc: '引擎级性能优化，开单查询快到飞起' },
        { icon: 'check', title: '更便捷的操作', desc: '智能化操作流程，省心省力' },
        { icon: 'sync', title: '更快迭代', desc: '更快上线新功能，持续成长' },
      ],
    }
  },
}
</script>

<template>
  <div class="compare-page">
    <section class="compare-hero">
      <div class="container">
        <span class="compare-hero__eyebrow">双产品对比</span>
        <h1>航富通 vs 航富通 Nova</h1>
        <p>同一套生意能力，Nova 让你更快、更省心、更先人一步</p>
      </div>
    </section>

    <!-- 进阶关系可视化 -->
    <section class="section section--white">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">版本关系</span>
          <h2>Nova 包含航富通全部能力，再进阶旗舰体验</h2>
          <p>两版本是「进阶」而非「并列」——核心生意逻辑一致，旗舰版体验更进一层</p>
        </div>

        <div class="ladder reveal">
          <div class="ladder__base">
            <div class="ladder__badge"><AppIcon name="store" :size="18" /></div>
            <h3>航富通（标准版）</h3>
            <p>开单收银 · 进销存 · 会员 · 报表 · 员工管理</p>
          </div>
          <div class="ladder__path">
            <span class="ladder__arrow-line"></span>
            <span class="ladder__arrow-label">＋ 旗舰进阶</span>
            <span class="ladder__arrow-line"></span>
          </div>
          <div class="ladder__top">
            <div class="ladder__badge"><AppIcon name="rocket" :size="18" /></div>
            <h3>航富通 Nova（旗舰版）</h3>
            <p>包含标准版全部能力 · 更快性能 · 更便捷操作 · 更快迭代新功能</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Nova 旗舰卖点 -->
    <section class="section section--light">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">旗舰亮点</span>
          <h2>为什么选择 Nova？</h2>
        </div>
        <div class="grid grid--3">
          <article v-for="(e, i) in novaEdges" :key="i" class="card edge reveal">
            <div class="icon-badge"><AppIcon :name="e.icon" /></div>
            <h3>{{ e.title }}</h3>
            <p>{{ e.desc }}</p>
          </article>
        </div>
      </div>
    </section>

    <!-- 对比表 -->
    <section class="section section--white">
      <div class="container">
        <div class="section-head reveal">
          <span class="section-eyebrow">详细对比</span>
          <h2>两个版本，一目了然</h2>
        </div>
        <div class="table-scroll reveal">
          <table class="compare-table">
            <thead>
              <tr>
                <th>维度</th>
                <th class="ct-standard"><AppIcon name="store" :size="18" /> 航富通（标准版）</th>
                <th class="ct-nova"><AppIcon name="rocket" :size="18" /> 航富通 Nova（旗舰版）</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in compareRows" :key="row.dimension">
                <td class="compare-table__dim">{{ row.dimension }}</td>
                <td>{{ row.standard }}</td>
                <td class="compare-table__nova">{{ row.nova }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="compare-cta reveal">
          <p>不确定选哪个？欢迎咨询，顾问会结合你的使用场景推荐</p>
          <button class="btn btn--primary" @click="open">立即咨询</button>
        </div>
      </div>
    </section>
  </div>
</template>


<style lang="scss" scoped>
.compare-page { padding-top: 72px; }

.compare-hero {
  background: $gradient-dark;
  color: $color-white;
  text-align: center;
  padding: 104px 0 88px;
  &__eyebrow { color: rgba(255,255,255,0.62); font-weight: $weight-semibold; letter-spacing: 0.12em; font-size: 13px; text-transform: uppercase; }
  h1 { color: $color-white; font-size: 48px; font-weight: $weight-black; margin: 16px 0 14px; letter-spacing: -0.02em; }
  p { color: rgba(255,255,255,0.72); font-size: 18px; }
}

.ladder {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
  max-width: 700px;
  margin: 0 auto;

  &__base, &__top {
    width: 100%;
    border-radius: $radius-lg;
    padding: 36px;
    text-align: center;
    border: 1px solid rgba(139, 92, 246, 0.16);
    box-shadow: 0 8px 26px rgba(139, 92, 246, 0.10);
    h3 { font-size: 24px; font-weight: $weight-bold; margin: 14px 0 8px; }
    p { color: $color-text-muted; }
  }

  &__base { background: linear-gradient(135deg, #F4EDFF 0%, #FBE9F6 100%); }
  &__top { background: linear-gradient(135deg, #FBE9F6 0%, #F1E9FF 100%); border-color: rgba(236, 72, 153, 0.20); box-shadow: 0 8px 26px rgba(236, 72, 153, 0.10); }

  &__badge {
    width: 44px; height: 44px; border-radius: 12px;
    background: linear-gradient(135deg, #8B5CF6, #EC4899);
    color: $color-white;
    display: flex; align-items: center; justify-content: center;
    margin: 0 auto;
    box-shadow: 0 6px 16px rgba(139, 92, 246, 0.22);
  }

  &__path { display: flex; align-items: center; gap: 12px; width: 80%; color: $color-accent; }
  &__arrow-line { flex: 1; height: 1px; background: $color-border; }
  &__arrow-label { font-weight: $weight-bold; font-size: 14px; white-space: nowrap; color: $color-accent; }
}

.edge {
  h3 { margin: 18px 0 8px; font-size: $font-card; font-weight: $weight-bold; }
  p { color: $color-text-muted; font-size: 15px; line-height: 1.6; }
}

.compare-table {
  width: 100%;
  border-collapse: collapse;
  background: $color-bg-white;
  border-radius: $radius-md;
  overflow: hidden;
  box-shadow: $shadow-sm;
  border: 1px solid rgba(29,29,31,0.05);

  th, td { padding: 18px 22px; border-bottom: 1px solid rgba(29,29,31,0.05); font-size: 15px; text-align: left; }
  th { font-size: 15px; font-weight: $weight-semibold; }
  th svg { vertical-align: middle; margin-right: 6px; }
  .ct-standard { background: $color-primary-soft; }
  .ct-nova { background: $color-secondary-soft; color: $color-primary; }
  &__dim { font-weight: $weight-bold; color: $color-text; }
  &__nova { color: $color-primary; font-weight: $weight-medium; }
}

.compare-cta {
  text-align: center;
  margin-top: 40px;
  p { color: $color-text-muted; margin-bottom: 18px; }
}

@media (max-width: $bp-sm) {
  .compare-hero h1 { font-size: 34px; }
  .ladder { padding: 0 16px; }
}
</style>



// WEBPACK FOOTER //
// src/site/views/CompareView.vue